import { getPageContext } from '@thoughtspot/blink-context';
import {
    EventStatus,
    IncomingEventType,
    isWebView,
} from '@thoughtspot/embed-util';
import { cloneDeep } from 'lodash';
import {
    componentHandledEvents,
    EventHandler,
    logger,
    MessageType,
    NON_MEMOIZED_EVENTS,
    OnMessageEvent,
    rootLevelEvents,
} from './constants';
import { eventHandlerMap } from './subscribe';
import { computeHash, getHostEventConfig, getParentOrigin } from './utils';
import { validateRequiredParams } from './validation';

// Wrapper to handle cases where feature flags/logger aren't available (e.g., in tests)
const safeIsWebView = (): boolean => {
    try {
        return isWebView();
    } catch {
        return false;
    }
};

export const callHandler = async (
    handlers: Set<EventHandler>,
    context: string,
    payload: any,
    event?: OnMessageEvent,
) => {
    const hostEventConfig = getHostEventConfig();
    // Bypass validation if:
    // 1. Using old host events (useHostEventsV2 = false) - always bypass for backward compatibility
    // 2. OR explicitly set to bypass (shouldBypassPayloadValidation = true) with V2 enabled
    const shouldBypassHostEventPayloadValidation =
        !hostEventConfig.useHostEventsV2 ||
        hostEventConfig.shouldBypassPayloadValidation === true;

    const handleSingleHandler = async (h: EventHandler) => {
        const ctxParams = h.requiredParams?.[context];
        try {
            // Validate params unless validation is bypassed or no schema exists
            const shouldValidate =
                !shouldBypassHostEventPayloadValidation && ctxParams;
            if (shouldValidate) {
                const isValid = validateRequiredParams(ctxParams, payload);
                if (!isValid) {
                    if (event?.ports?.[0]) {
                        event.ports[0].postMessage(
                            cloneDeep({
                                error: `Invalid payload for context: ${context}, required parameters: ${JSON.stringify(
                                    ctxParams,
                                )}`,
                            }),
                        );
                    }
                    return;
                }
            }

            const data = await h.callback(payload);
            // Only send response if handler returns data (matching original behavior)
            // Sending empty responses can break chart initialization
            if (data && event?.ports?.[0]) {
                event.ports[0].postMessage(cloneDeep(data));
            }
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : String(err);
            logger.error(
                `[callHandler] Error in handler for context ${context}, required parameters: ${JSON.stringify(
                    ctxParams,
                )}:`,
                err,
            );

            if (event?.ports?.[0]) {
                event.ports[0].postMessage(
                    cloneDeep({
                        error: errorMessage,
                        context,
                        requiredParams: ctxParams,
                    }),
                );
            }
        }
    };

    // Process handlers sequentially to maintain order
    const handlerArray = Array.from(handlers);
    await handlerArray.reduce(
        (promise, h) => promise.then(() => handleSingleHandler(h)),
        Promise.resolve(),
    );
};

export const contextsMatch = (
    handlerContext: any,
    targetContext: any,
): boolean => {
    if (handlerContext === targetContext) return true;

    if (handlerContext === 'root') return false;

    if (typeof handlerContext === 'object' && handlerContext?.page) {
        return handlerContext.page === targetContext;
    }

    return false;
};

/**
 * Checks if an event type should be handled by a component directly
 * rather than through the event bridge subscription system.
 * Component-handled events bypass the bridge and are forwarded to component listeners.
 *
 * @param eventType - The event type to check
 * @param context - The context to check
 * @returns True if the event should be handled by a component
 */
export const isComponentHandledEvent = (eventType: string): boolean => {
    return componentHandledEvents.has(eventType);
};

export const onMessage = async (event: OnMessageEvent) => {
    const hostEventConfig = getHostEventConfig();
    const isHostEventsV2Enabled = hostEventConfig.useHostEventsV2;
    const { type, context: passedContext, data } = event.data ?? {};

    const pageContext = getPageContext();
    const context = passedContext || pageContext?.page;

    const handlers = eventHandlerMap.get(type);
    const isRootLevel = rootLevelEvents.has(type as IncomingEventType);

    // Check if this event should be handled by a component directly
    // Component-handled events bypass the event bridge and are forwarded to component listeners
    if (isComponentHandledEvent(type)) {
        if (context && context !== componentHandledEvents.get(type)) {
            logger.debug(
                `Context ${context} does not match required context ${componentHandledEvents.get(
                    type,
                )}`,
            );
            return;
        }

        // Don't intercept - the component's message listener will handle it
        // The event bridge's onMessage is registered on window, and the component also
        // listens on window, so both will receive it. We just need to not block it here.
        // Return early without responding, let the component handle it and respond via port
        return;
    }
    if (!handlers || handlers.size === 0) {
        logger.debug(`No Action available for ${type}, try changing the context`);
        return;
    }

    if (rootLevelEvents.has(type as IncomingEventType)) {
        const handlerArray = Array.from(handlers);
        await handlerArray.reduce(async (promise, h) => {
            await promise;
            let results: any;
            try {
                results = await h.callback(data, context);
            } catch (err) {
                results = { error: err };
            }
            // Only send response if handler returns data (matching original behavior)
            if (results && event?.ports?.[0]) {
                event.ports[0].postMessage(cloneDeep(results));
            }
        }, Promise.resolve());
        return;
    }

    let matching: EventHandler[] = [];
    if (!isHostEventsV2Enabled) {
        matching = Array.from(handlers);
    } else {
        matching = Array.from(handlers).filter(h =>
            contextsMatch(h.context, context),
        );
    }

    if (matching.length === 0) {
        logger.debug(`No Action available for ${type}, try changing the context`);
        return;
    }

    await callHandler(new Set(matching), context, data, event);
};

export const destroyHandlers = () => {
    window.removeEventListener('message', onMessage);
};

export const dispatchEvent = (nonMemoizedEventTypes?: Set<string>) => {
    const memo = new Map<MessageType, string>();
    return (type: MessageType, payload: any, status = EventStatus.End) => {
        if (window === window.parent && !safeIsWebView()) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            const channel = new window.MessageChannel();
            channel.port1.onmessage = ({ data }) => {
                channel.port1.close();
                if (data.error) {
                    reject(data.error);
                } else {
                    resolve(data);
                }
            };

            if (safeIsWebView()) {
                const listener = (event: any) => {
                    if (event.data.type === type) {
                        resolve(event.data || {});
                    } else {
                        reject(event.data || {});
                    }
                    window.removeEventListener('message', listener);
                };
                window.addEventListener('message', listener);
            }

            let dispatchRepeat = nonMemoizedEventTypes?.has(type);
            if (!dispatchRepeat) {
                const hash = computeHash(payload);
                const prevHash = memo.get(type);

                dispatchRepeat = !prevHash || hash !== prevHash;
                if (dispatchRepeat) {
                    memo.set(type, hash);
                }
            }
            const parentOrigin = getParentOrigin();

            try {
                if (dispatchRepeat && parentOrigin !== null) {
                    if (safeIsWebView()) {
                        (window as any).ReactNativeWebView?.postMessage(
                            JSON.stringify({
                                type,
                                data: cloneDeep(payload),
                                status,
                            }),
                        );
                        resolve({});
                    } else {
                        window.parent.postMessage(
                            { type, data: cloneDeep(payload), status },
                            parentOrigin,
                            [channel.port2],
                        );
                    }
                }
            } catch (error) {
                logger.error('Error dispatching event', error);
            }
        });
    };
};

export const dispatchNonMemoEvent = dispatchEvent(new Set(NON_MEMOIZED_EVENTS));